import { dataUrlToBase64, dataUrlToFile, PreparedFileUpload } from 'lib/utils/dataUrl';
import { StoredFileUpload } from 'verify/lib/interfaces/fileUploads';

/**
 * A promise-based wrapper for `FileReader`. Takes a file and returns a promise
 * that resolves to the loaded file.
 */
export function readFile(file: File, asText = false): Promise<string | ArrayBuffer> {
  return new Promise((res, rej) => {
    if (typeof FileReader === 'undefined') {
      rej(new Error('FileReader is not supported in this environment.'));
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      if (e.target?.result == null) {
        rej(new Error('FileReader failed to load the file.'));
        return;
      }
      res(e.target.result);
    };
    reader.onerror = (e) => {
      rej(e.target?.error ?? new Error('An unknown error occurred while reading the file.'));
      reader.abort();
    };

    if (asText) {
      reader.readAsText(file);
    } else {
      reader.readAsDataURL(file);
    }
  });
}

/**
 * Prepares a StoredFileUpload for submission to the server.
 */
export function createAttachment(upload: StoredFileUpload, multipart = true) {
  return multipart
    ? dataUrlToFile(upload)
    : {
        filename: upload.filename,
        data: dataUrlToBase64(upload.dataUrl),
      };
}

export function createAttachments(
  uploads: StoredFileUpload[] = [],
  multipart = true,
): PreparedFileUpload[] {
  return uploads.map((upload) => createAttachment(upload, multipart));
}

export function blobToFile(blobs: Blob[], fileName: string, fileType: string): PreparedFileUpload {
  try {
    // Old versions of Edge, and very old versions of Safari don't have a File constructor.
    return new File(blobs, fileName, { type: fileType });
  } catch {
    // Default to a blob if no file constructor exists
    return new Blob(blobs, { type: fileType });
  }
}

// 10 MB
export const LARGE_FILE_BYTES = 10000000;

export enum FileType {
  ImageJpg = 'image/jpg',
  ImageJpeg = 'image/jpeg',
  ImagePng = 'image/png',
  ImageHeic = 'image/heic',
  ImageHeif = 'image/heif',
  ImageTif = 'image/tif',
  ImageTiff = 'image/tiff',
  ImageGif = 'image/gif',
  ImageSvgXml = 'image/svg+xml',
  ApplicationPdf = 'application/pdf',
  TextPlain = 'text/plain',
  VideoMp4 = 'video/mp4',
  VideoWebm = 'video/webm',
}

export function fileTypeToExtension(fileType: FileType) {
  switch (fileType) {
    case FileType.ImageSvgXml:
      return 'svg';
    case FileType.TextPlain:
      return 'txt';
    case FileType.VideoMp4:
      return 'mp4';
    case FileType.VideoWebm:
      return 'webm';
    default:
      return fileType.split('/')[1];
  }
}
